import { checkAccount, getAccessToken } from './auth';
import { noSpotify } from './flags';
import type { MusicService } from '@banger/shared';

/**
 * 'unplayable': this track can't play (restriction, unknown track, playback_error).
 * 'device_error': the player/session is broken (token, network, device gone). Never the song's fault.
 */
export type PlayResult = 'ok' | 'unplayable' | 'device_error';

export interface BangerPlayer {
  /** The service this player plays; the display never hands it another service's track. */
  readonly service: MusicService;
  deviceId: string;
  /** `trackId` is the id on the service this player belongs to (the room's service). */
  play(trackId: string, startMs: number): Promise<PlayResult>;
  pause(): Promise<void>;
  resume(): Promise<void>;
  activate(): Promise<void>;
  disconnect(): void;
}
export type PlayerError = 'not_allowed' | 'not_premium' | 'sdk_failed';

const SDK_URL = 'https://sdk.scdn.co/spotify-player.js';
const PLAY_URL = 'https://api.spotify.com/v1/me/player/play';
const PLAY_CONFIRM_MS = 5_000;
const READY_TIMEOUT_MS = 15_000;

let sdkLoading: Promise<void> | null = null;

function loadSdk(): Promise<void> {
  if (window.Spotify) return Promise.resolve();
  sdkLoading ??= new Promise<void>((resolve, reject) => {
    window.onSpotifyWebPlaybackSDKReady = () => resolve();
    const s = document.createElement('script');
    s.src = SDK_URL;
    s.async = true;
    s.onerror = () => { sdkLoading = null; s.remove(); reject(new Error('Spotify SDK failed to load')); };
    document.head.appendChild(s);
  });
  return sdkLoading;
}

/** Spoiler hygiene: keep OS media widgets from showing the song. Best-effort. */
function hideMediaMetadata(): void {
  try {
    navigator.mediaSession.metadata = new MediaMetadata({ title: 'BANGER!', artist: '' });
  } catch { /* unsupported */ }
}

function isTrack(s: Spotify.PlaybackState, uri: string): boolean {
  const t = s.track_window.current_track;
  return !!t && (t.uri === uri || t.linked_from?.uri === uri);
}

const noopPlayer: BangerPlayer = {
  service: 'spotify',
  deviceId: 'nospotify',
  play: async () => 'ok',
  pause: async () => {},
  resume: async () => {},
  activate: async () => {},
  disconnect: () => {},
};

/**
 * `onFatal` reports errors after the player was ready: account/auth problems
 * (show InviteOnly) or a dead SDK ('sdk_failed': offer to reconnect).
 */
export async function createPlayer(
  onFatal: (e: PlayerError) => void = () => {},
): Promise<BangerPlayer | { error: PlayerError }> {
  if (noSpotify()) return noopPlayer;

  try {
    const account = await checkAccount();
    if (account !== 'ok') return { error: account };
    await loadSdk();
  } catch {
    return { error: 'sdk_failed' };
  }

  const sdk = new window.Spotify!.Player({
    name: 'BANGER!',
    getOAuthToken: (cb) => { getAccessToken().then(cb, () => { /* surfaces as authentication_error */ }); },
    volume: 0.8,
  });

  let deviceId = '';
  let isReady = false;
  let live = false; // initial setup finished; errors now go to onFatal
  let readyWaiters: ((ok: boolean) => void)[] = [];
  let initFail: (e: PlayerError) => void = () => {};

  const fail = (e: PlayerError) => { if (live) onFatal(e); else initFail(e); };

  sdk.addListener('ready', (d) => {
    deviceId = d.device_id;
    isReady = true;
    for (const w of readyWaiters) w(true);
    readyWaiters = [];
  });
  sdk.addListener('not_ready', () => { isReady = false; });
  sdk.addListener('account_error', () => fail('not_premium'));
  sdk.addListener('initialization_error', () => fail('sdk_failed'));
  sdk.addListener('authentication_error', () => {
    // Mid-party (the account already played) it's a lost session: offer to reconnect.
    if (live) return onFatal('sdk_failed');
    // At setup, a fresh token that is still refused means the account isn't allow-listed.
    getAccessToken(Date.now(), true).then(() => fail('not_allowed'), () => fail('sdk_failed'));
  });

  let waiting: { uri: string; settle: (r: PlayResult) => void } | null = null;
  sdk.addListener('player_state_changed', (s) => {
    if (waiting && s && !s.paused && isTrack(s, waiting.uri)) waiting.settle('ok');
  });
  sdk.addListener('playback_error', () => waiting?.settle('unplayable'));

  function waitReady(): Promise<boolean> {
    return new Promise((resolve) => {
      readyWaiters.push(resolve);
      setTimeout(() => resolve(false), READY_TIMEOUT_MS);
    });
  }

  let reconnecting: Promise<boolean> | null = null;
  function reconnect(): Promise<boolean> {
    reconnecting ??= (async () => {
      isReady = false;
      sdk.disconnect();
      const ready = waitReady();
      return (await sdk.connect()) && (await ready);
    })().finally(() => { reconnecting = null; });
    return reconnecting;
  }

  const initial = await new Promise<boolean | PlayerError>((resolve) => {
    initFail = resolve;
    waitReady().then(resolve);
    sdk.connect().then((ok) => { if (!ok) resolve('sdk_failed'); }, () => resolve('sdk_failed'));
  });
  if (initial !== true) {
    sdk.disconnect();
    return { error: initial === false ? 'sdk_failed' : initial };
  }
  live = true;

  /** One PUT. null = accepted (now wait for the SDK to confirm). */
  async function put(uri: string, startMs: number): Promise<number | 'network' | null> {
    let token: string;
    try { token = await getAccessToken(); } catch { return 'network'; }
    try {
      const res = await fetch(`${PLAY_URL}?device_id=${encodeURIComponent(deviceId)}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ uris: [uri], position_ms: startMs }),
      });
      return res.ok ? null : res.status;
    } catch {
      return 'network';
    }
  }

  return {
    service: 'spotify',
    get deviceId() { return deviceId; },
    async play(spotifyId, startMs) {
      const uri = `spotify:track:${spotifyId}`;
      waiting?.settle('ok'); // superseded; the caller ignores stale results
      let done = false;
      let settle!: (r: PlayResult) => void;
      const result = new Promise<PlayResult>((r) => { settle = (x) => { done = true; r(x); }; });
      const me = { uri, settle };
      waiting = me; // before the PUT: the state event can beat the response
      let timer: ReturnType<typeof setTimeout> | undefined;

      void (async () => {
        if (!isReady && !(await reconnect())) return settle('device_error');
        let status = await put(uri, startMs);
        if (status === 404) {
          // Usually a stale device: reconnect once and retry before blaming the song.
          if (!(await reconnect())) return settle('device_error');
          status = await put(uri, startMs);
          if (status === 404) return settle('unplayable');
        }
        if (status === 'network') return settle('device_error');
        if (status === 400 || status === 403) return settle('unplayable'); // bad/restricted track
        if (status !== null) return settle('device_error'); // 401, 429, 5xx
        if (!done) timer = setTimeout(() => settle('unplayable'), PLAY_CONFIRM_MS); // starts after the PUT
      })().catch(() => settle('device_error')); // e.g. sdk.connect() threw: never stall the queue

      const r = await result;
      clearTimeout(timer);
      if (waiting === me) waiting = null;
      if (r === 'ok') hideMediaMetadata();
      return r;
    },
    pause: () => sdk.pause(),
    resume: () => sdk.resume(),
    activate: () => sdk.activateElement(),
    disconnect: () => { live = false; onFatal = () => {}; sdk.disconnect(); },
  };
}
